import {ChangeEvent, useState} from 'react';
import {MediumText, RegularText} from 'shared/components/text';

import useCart from 'modules/cart/util/use-cart';
import {
  PaymentMethodProps,
  PaymentTypeEnum,
} from 'modules/payment-methods/types';
import Column from 'shared/components/column';
import {Container, Content, Input, PaymentOption, PixInput} from './styles';
import useCompanyPaymentMethods from 'modules/companies/hooks/use-company-payment-methods';
import {useNavigate, useParams} from 'react-router-dom';
import {errorNotification} from 'shared/services/notification';
import Modal from 'shared/migration-components/modal';
import toBrl from 'shared/util/to-brl';
import Row from 'shared/components/row';
import Button from 'shared/migration-components/button';
import Loading from 'shared/migration-components/loading';
import Payment from './components/payment';
import Mask from 'shared/util/mask';

const PaymentMethods = () => {
  const {companyId} = useParams<{
    companyId: string;
  }>();
  const {isLoading, data} = useCompanyPaymentMethods(companyId);
  const [bottomSheet, showBottomSheet] = useState(false);
  const [change, setChange] = useState<number>();
  const [pixChange, setPixChange] = useState<string>();
  const {
    addPayment,
    total,
    discount,
    cart,
    deliveryPriceQuery,
    freeDeliveryThreshold,
    deliveryPrice,
  } = useCart();
  const navigate = useNavigate();
  const [numericChange, setNumericChange] = useState<string>();

  const paymentMethods = data.map((payment) => payment.data!);

  const inPersonPaymentMethods = paymentMethods.filter(
    (payment) => payment.type === PaymentTypeEnum.IN_PERSON,
  );

  const onlinePaymentMethods = paymentMethods.filter(
    (payment) => payment.type === PaymentTypeEnum.ONLINE,
  );

  const handlePayment = (payment: PaymentMethodProps) => {
    if (payment.name === 'Dinheiro') return showBottomSheet(true);

    addPayment(payment);
    navigate(-1);
  };

  const handleMoneyPayment = (changeNeeded = true) => {
    if (!change && changeNeeded)
      return errorNotification({
        title: 'Erro ao confirmar pagamento',
        message: 'Troco inválido',
      });

    if (change && changeNeeded && !pixChange)
      return errorNotification({
        title: 'Erro ao confirmar pagamento',
        message: 'Informe o pix para receber o troco',
      });

    const payment = {
      name: 'Dinheiro',
      change: changeNeeded ? change : undefined,
      pixChange: change && pixChange ? pixChange : undefined,
    };

    addPayment(payment);

    navigate(-1);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const numericValue = value.replace(/[^\d]/g, '');

    setNumericChange(numericValue);

    const integer = numericValue.slice(0, -2) || '0';
    const decimal = numericValue.slice(-2);

    setChange(+String(`${+integer}.${decimal}`));
  };

  return (
    <Container>
      {isLoading && <Loading flex />}
      {!isLoading && (
        <Content>
          <MediumText size="medium">Pagamento na entrega</MediumText>
          <Column gap={15}>
            {inPersonPaymentMethods.map((payment) => (
              <PaymentOption
                key={payment.id}
                onClick={() => handlePayment(payment)}>
                <Row gap={10}>
                  <Payment {...payment} />
                </Row>
              </PaymentOption>
            ))}
          </Column>
          <MediumText size="medium" style={{marginTop: 15}}>
            Pagamento online
          </MediumText>
          <Column gap={15}>
            {onlinePaymentMethods.map((payment) => (
              <PaymentOption
                key={payment.id}
                onClick={() => handlePayment(payment)}>
                <Payment {...payment} />
              </PaymentOption>
            ))}
          </Column>
        </Content>
      )}
      {bottomSheet && (
        <Modal
          opened
          centered
          size={700}
          withinPortal
          onClose={() => showBottomSheet(false)}
          overlayBackgroundColor="rgba(0, 0, 0, .2)">
          <Column gap={10} alignItems="center">
            <MediumText size="large">Vai precisar de troco?</MediumText>
            <RegularText style={{textAlign: 'center'}}>
              Digite o valor que você vai pagar em dinheiro e informe o pix que
              deseja receber o troco. Total do pedido{' '}
              {toBrl(
                total -
                  discount -
                  (cart?.balance ?? 0) +
                  (deliveryPriceQuery.data?.freeDelivery ||
                  freeDeliveryThreshold
                    ? 0
                    : deliveryPrice),
              )}
              .
            </RegularText>
            <Input
              placeholder="Ex: R$ 50,00"
              value={
                numericChange ? Mask.format('brl', String(numericChange)) : ''
              }
              onChange={onChange}
            />
            <PixInput
              placeholder="Informe o pix para receber o troco"
              onChange={(e) => setPixChange(e.target.value)}
            />
            <Row gap={15}>
              <Button
                color="textColor"
                title="Não preciso de troco"
                onClick={() => handleMoneyPayment(false)}
              />
              <Button title="Confirmar" onClick={handleMoneyPayment} />
            </Row>
          </Column>
        </Modal>
      )}
    </Container>
  );
};

export default PaymentMethods;
